import React, { ChangeEvent } from 'react';
import Inputs from 'components/Inputs';
import Row from '../../Common/Row';
import Button from 'kingpin/atoms/Button';
import Form from '../../../kingpin/forms/Form';
import FormHeader from '../../../kingpin/forms/FormHeader';
import FormContent from '../../../kingpin/forms/FormContent';
import EntityFilterToggle from '../../EntityFilterToggle';
import FormInput from '../../../kingpin/forms/FormInput';
import Tooltip from '../../Tooltip';
import Icon from '../../../kingpin/atoms/Icon';
import LabelsPicker from '../../Inputs/LabelsPicker';
import FleetOpsStaffOnly from '../../Common/FleetOpsStaffOnly';

const NONE_FIELD_VALUE = '__none__';

const SettingsPopup = ({
  name,
  onNameChanged,
  isEntityFilterEnabled,
  setIsEntityFilterEnabled,
  isAsset,
  setIsAsset,
  labelIds,
  setLabelIds,
  sortField,
  setSortField,
  sortDirection,
  setSortDirection,
  sortFieldOptions,
  pastWeeks,
  setPastWeeks,
  futureWeeks,
  setFutureWeeks,
  onSave,
  isLoading,
  close,
}: {
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  isEntityFilterEnabled: boolean;
  setIsEntityFilterEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isAsset: boolean;
  setIsAsset: React.Dispatch<React.SetStateAction<boolean>>;
  labelIds: string[];
  setLabelIds: React.Dispatch<React.SetStateAction<string[]>>;
  sortField: string | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string | undefined>>;
  sortDirection: SortBy;
  setSortDirection: React.Dispatch<React.SetStateAction<SortBy>>;
  sortFieldOptions: { field: string; label: string }[];
  pastWeeks: number | undefined;
  setPastWeeks: React.Dispatch<React.SetStateAction<number | undefined>>;
  futureWeeks: number | undefined;
  setFutureWeeks: React.Dispatch<React.SetStateAction<number | undefined>>;
  onSave: () => void;
  isLoading: boolean;
  close: () => void;
}) => {
  const fieldDropdownOptions: DropdownOption[] = [
    {
      key: NONE_FIELD_VALUE,
      label: 'None',
      value: NONE_FIELD_VALUE,
      isSelected: !sortField,
      onSelected: () => setSortField(undefined),
    },
    ...sortFieldOptions.map((o) => ({
      key: o.field,
      label: o.label,
      value: o.field,
      isSelected: sortField === o.field,
      onSelected: () => setSortField(o.field),
    })),
  ];

  const directionDropdownOptions: DropdownOption[] = (
    ['asc', 'desc'] as SortBy[]
  ).map((direction) => ({
    key: direction,
    label: direction === 'asc' ? 'Ascending' : 'Descending',
    value: direction,
    isSelected: sortDirection === direction,
    onSelected: () => setSortDirection(direction),
  }));

  return (
    <Form>
      <FormHeader title="Board Settings" onClose={close} />
      <FormContent>
        <FormInput label={'Name'}>
          <Inputs.TextInput
            value={name}
            onChange={onNameChanged}
            inputSize="Small"
          />
        </FormInput>
        <FleetOpsStaffOnly>
          <FormInput label={'Is Asset'}>
            <Row centerAlign style={{ gap: 4 }}>
              <Inputs.Checkbox isChecked={isAsset} onToggled={setIsAsset} />
              <div style={{ cursor: 'help' }}>
                <Tooltip
                  content={
                    "Boards which are an 'Asset' will show in the 'View all Content' and 'All Assets' screens in V3 to those users who have access to them"
                  }
                >
                  <Icon icon={'help'} />
                </Tooltip>
              </div>
            </Row>
          </FormInput>
          <>
            {isAsset && (
              <FormInput label={'Labels'}>
                <LabelsPicker labelIds={labelIds} setLabelIds={setLabelIds} />
              </FormInput>
            )}
          </>
        </FleetOpsStaffOnly>
        <FormInput label={'Order table by'}>
          <Row style={{ gap: 8, width: '100%' }}>
            <div style={{ flex: 1 }}>
              <Inputs.Dropdown
                options={fieldDropdownOptions}
                placeholder="None"
              />
            </div>
            <div style={{ flex: 1 }}>
              <Inputs.Dropdown
                options={directionDropdownOptions}
                isDisabled={!sortField}
              />
            </div>
          </Row>
        </FormInput>
        <FormInput label={'Period window'}>
          <Row style={{ gap: 8, width: '100%' }}>
            <div style={{ flex: 1 }}>
              <FormInput label={'Weeks to look back'}>
                <Inputs.Number value={pastWeeks} setValue={setPastWeeks} />
              </FormInput>
            </div>
            <div style={{ flex: 1 }}>
              <FormInput label={'Weeks ahead'}>
                <Inputs.Number value={futureWeeks} setValue={setFutureWeeks} />
              </FormInput>
            </div>
          </Row>
        </FormInput>
        <EntityFilterToggle
          isEntityFilterEnabled={isEntityFilterEnabled}
          setIsEntityFilterEnabled={setIsEntityFilterEnabled}
        />
      </FormContent>
      <Row style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="Primary"
          size="Small"
          onClick={onSave}
          isLoading={isLoading}
          isDisabled={isLoading}
          label="Save"
        />
      </Row>
    </Form>
  );
};

export default SettingsPopup;
